import getAppConfig from "@liberty-fund/config";
import { Logger } from "@liberty-fund/logger";
import { getAccessToken } from "@liberty-fund/utils/auth";
import { ClientError, GraphQLClient } from "graphql-request";
import { TypedDocumentString } from "./generated/graphql";

/**
 * Calls the GraphQL API directly from the browser.
 *
 * This used to POST to the portal's own `/api/graphql` route, which existed
 * solely to translate the ALB's `x-amzn-oidc-accesstoken` header into an
 * `Authorization` header. With Auth0 running in the browser the token is
 * already here, so the hop is gone — which means the API must send CORS headers
 * for the portal origin and accept Auth0 access tokens issued for
 * `config.auth0.audience`.
 */
const graphQLFetcher = async <T>(
  query: TypedDocumentString<T, any>,
  variables: Record<string, any> = {},
) => {
  const config = getAppConfig();
  const url = config.aws.graphql.url;
  const accessToken = await getAccessToken();

  // Use errorPolicy:'all' so graphql-request v7 does not throw on GraphQL-level
  // errors before we can inspect them — partial data (e.g. an unauthorized field)
  // should still be returned to the caller.
  const isMutation = query.toString().trimStart().startsWith("mutation");
  const client = new GraphQLClient(url, {
    errorPolicy: "all",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  Logger.debug("graphQLFetcher", { url, query: query.toString(), variables });

  const { data, errors, status } = await client.rawRequest<T>({
    query: query.toString(),
    variables,
  });

  Logger.debug("graphQLResponse", { data });

  if (errors?.length) {
    // Mutations must not silently swallow errors: a null result looks like success to callers.
    if (isMutation || !data) {
      Logger.error("graphQLFetcher error", { errors, status });
      throw new ClientError(
        { data, errors, status },
        { query: query.toString(), variables },
      );
    }
    // Partial query data (e.g. an unauthorized field): return what we have.
    // Fields nulled by AppSync may not satisfy TypeScript non-null types at runtime.
    Logger.warn("graphQLFetcher partial errors (returning data)", {
      errors,
      status,
    });
  }

  return data as T;
};

export default graphQLFetcher;

// Export hooks
export * from "./hooks";

// Export generated types and documents
export * from "./generated/graphql";

export { toTaxDocumentFormat } from "./taxDocumentFormat";